import React, { useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import * as Sentry from "@sentry/react";
import "react-toastify/dist/ReactToastify.css";
import { GoogleLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import { FaChevronLeft } from "react-icons/fa";
import StorageService from "../services/storageService";
import { UserContext } from "../contexts/UserContext";
import {
  JOURNEY_MODAL_FLAG,
  ONBOARDING_CHECKLIST,
  SOCIAL_SIGNUP_DETAILS,
} from "../constants/storageKeys";
import { analytics, logEvent, setUserId } from "../firebaseConfig";
import AuthServerErrorModal from "./AuthServerErrorModal";

// Define the backend API URL.
const baseUrl = import.meta.env.VITE_APP_BASE_URL;

export default function AuthForm({ setIsLogged }) {
  const [isLogin, setIsLogin] = useState(true);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [fullName, setFullName] = useState("");
  const [handle, setHandle] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [serverErrorDetail, setServerErrorDetail] = useState(null);
  // const googleButtonRef = useRef(null);
  const userContext = useContext(UserContext) || {};
  const {
    updateProfileDetails: setUserContextProfile,
    fetchUserProfile: refetchUserProfile,
  } = userContext;

  const normalizeProfileValue = (value) => {
    if (value === null || value === undefined) return undefined;
    const normalized = String(value).trim();
    return normalized.length ? normalized : undefined;
  };

  const hydrateUserContext = (profile = {}) => {
    if (typeof setUserContextProfile !== "function" || !profile) return;

    const normalizedFullName =
      normalizeProfileValue(profile.fullName) ||
      normalizeProfileValue(profile.name);

    setUserContextProfile({
      userId: normalizeProfileValue(profile.userId) || profile.userId,
      name: normalizedFullName,
      fullName: normalizedFullName,
      email: normalizeProfileValue(profile.email),
      handle: normalizeProfileValue(profile.handle),
      roleType: normalizeProfileValue(profile.role),
      role: normalizeProfileValue(profile.role),
      is_verified:
        typeof profile.is_verified === "boolean"
          ? profile.is_verified
          : undefined,
      createdAt: profile.createdAt || undefined,
      contactNumber: normalizeProfileValue(profile.contactNumber),
      school: normalizeProfileValue(profile.school),
      city: normalizeProfileValue(profile.city),
      board: normalizeProfileValue(profile.board),
      grade: normalizeProfileValue(profile.grade),
      profilePhoto: profile.profilePhoto || undefined,
      pincode: normalizeProfileValue(profile.pincode),
      goals: Array.isArray(profile.goals) ? profile.goals : undefined,
      onboarding_checklist:
        profile.onboarding_checklist &&
        typeof profile.onboarding_checklist === "object"
          ? profile.onboarding_checklist
          : undefined,
      learning:
        profile.learning && typeof profile.learning === "object"
          ? profile.learning
          : undefined,
      learning_current_level:
        profile.learning_current_level &&
        typeof profile.learning_current_level === "object"
          ? profile.learning_current_level
          : undefined,
      token: profile.token || undefined,
    });
  };

  const syncUserContextFromServer = async () => {
    if (typeof refetchUserProfile !== "function") return;
    try {
      await refetchUserProfile();
    } catch (syncError) {
      console.error("Failed to refresh user profile after auth:", syncError);
    }
  };

  const rememberSignupDetailsForOnboarding = (details = {}) => {
    if (typeof window === "undefined" || !details) {
      return;
    }
    try {
      const normalized = {
        ...details,
        fullName: details.fullName || details.name || "",
      };
      localStorage.setItem(SOCIAL_SIGNUP_DETAILS, JSON.stringify(normalized));
    } catch (storageError) {
      console.error(
        "Unable to persist signup details for onboarding:",
        storageError
      );
    }
  };

  const persistLearningState = (
    learningData = {},
    learningCurrentLevel = {}
  ) => {
    try {
      if (typeof window === "undefined") {
        return;
      }
      localStorage.setItem(
        "learning",
        JSON.stringify(learningData || {})
      );
      localStorage.setItem(
        "learning_current_level",
        JSON.stringify(learningCurrentLevel || {})
      );
    } catch (error) {
      console.error("Failed to persist learning state:", error);
    }
  };

  const persistOnboardingChecklist = (checklist) => {
    if (typeof window === "undefined") {
      return false;
    }

    try {
      if (checklist && typeof checklist === "object") {
        localStorage.setItem(
          ONBOARDING_CHECKLIST,
          JSON.stringify(checklist || {})
        );
        const hasPendingSteps =
          checklist.self_assessment === false ||
          checklist.evaluated_answersheet === false;
        localStorage.setItem(
          JOURNEY_MODAL_FLAG,
          hasPendingSteps ? "true" : "false"
        );
        return hasPendingSteps;
      }
      localStorage.removeItem(ONBOARDING_CHECKLIST);
      localStorage.setItem(JOURNEY_MODAL_FLAG, "false");
    } catch (storageError) {
      console.error("Unable to persist onboarding checklist:", storageError);
    }
    return false;
  };

  const isMobileViewport = () =>
    typeof window !== "undefined" &&
    window.matchMedia("(max-width: 767px)").matches;

  const getPostLoginPath = () => (isMobileViewport() ? "/tutor" : "/");
  const getProfileUpdatePath = () =>
    isMobileViewport() ? "/tutor" : "/profile-update";
  const getOnboardingPath = () => "/onboarding";

  const toggleForm = () => {
    setIsLogin(!isLogin);
    setError("");
    setServerErrorDetail(null);
  };

  const showLanding = () => {
    setShowEmailForm(false);
    setError("");
    setServerErrorDetail(null);
  };

  const showEmailLogin = () => {
    setShowEmailForm(true);
    setError("");
    setServerErrorDetail(null);
  };

  const tryShowServerError = (apiError) => {
    const detailCandidate = apiError?.response?.data?.detail;
    if (!detailCandidate || typeof detailCandidate !== "object") {
      return false;
    }

    setServerErrorDetail({
      ...detailCandidate,
      error:
        detailCandidate.error ||
        apiError?.response?.data?.error ||
        "Request blocked",
      message:
        detailCandidate.message ||
        apiError?.response?.data?.message ||
        "Please try again later or contact us.",
      code: detailCandidate.code || apiError?.response?.data?.code,
    });
    return true;
  };

  // useEffect(() => {
  //   if (
  //     typeof window === "undefined" ||
  //     typeof MutationObserver === "undefined" ||
  //     !googleButtonRef.current
  //   ) {
  //     return undefined;
  //   }

  //   const enforceGoogleButtonWidth = () => {
  //     const container = googleButtonRef.current;
  //     if (!container) return;
  //     const iframe = container.querySelector("iframe");
  //     const buttonRoot = container.querySelector("div[role='button']");
  //     const applyStyles = (el) => {
  //       if (!el) return;
  //       el.style.width = "100%";
  //       el.style.maxWidth = "100%";
  //       el.style.minWidth = "100%";
  //     };
  //     applyStyles(iframe);
  //     applyStyles(buttonRoot);
  //   };

  //   enforceGoogleButtonWidth();

  //   const observer = new MutationObserver(() => {
  //     enforceGoogleButtonWidth();
  //   });

  //   observer.observe(googleButtonRef.current, {
  //     childList: true,
  //     subtree: true,
  //     attributes: true,
  //   });

  //   return () => observer.disconnect();
  // }, []);

  // Google OAuth handlers
  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const { credential } = credentialResponse || {};
      if (!credential) {
        toast.error("Google login failed: Empty credential.");
        return;
      }
      // Decode the Google ID token to extract basic profile info
      const payload = jwtDecode(credential);
      const { sub: googleSub, email: gEmail, name: gName } = payload || {};

      if (!googleSub || !gEmail) {
        toast.error("Google login failed: Invalid token payload.");
        return;
      }

      setServerErrorDetail(null);

      // Call ONLY the signup API with google token included
      const signupPayload = {
        userId: googleSub,
        fullName: gName || "",
        email: (gEmail || "").toLowerCase(),
        role: "student",
        handle: gName || "",
        googleToken: credential,
      };

      try {
        const resp = await axios.post(
          `${baseUrl}/v2/auth/signup`,
          signupPayload
        );
        if (resp?.status === 200) {
          const data = resp.data || {};
          rememberSignupDetailsForOnboarding({
            ...data,
            email: data.email || gEmail || "",
            fullName: data.fullName || gName || "",
            userId: data.userId || googleSub,
          });
          const backendUserId = data.userId || googleSub;
          const backendEmail = data.email || gEmail;
          const backendFullName = data.fullName || gName || "Friend";
          const token = data.token || credential;
          await handlePostAuthSuccess({
            userId: backendUserId,
            email: backendEmail,
            fullName: backendFullName,
            token,
            onboardingChecklist: data?.onboarding_checklist || null,
          });
          return;
        }
      } catch (err) {
        if (err?.response?.status === 400) {
          // User exists: backend returns all details; store and redirect like post-login
          const data = err.response.data || {};
          rememberSignupDetailsForOnboarding({
            ...data,
            email: data.email || gEmail || "",
            fullName: data.fullName || gName || "",
            userId: data.userId || googleSub,
          });
          const backendUserId = data.userId || googleSub;
          const backendEmail = data.email || gEmail;
          const backendFullName = data.fullName || gName || "Friend";
          const token = data.token || credential;
          await handlePostAuthSuccess({
            userId: backendUserId,
            email: backendEmail,
            fullName: backendFullName,
            token,
            onboardingChecklist: data?.onboarding_checklist || null,
          });
          return;
        }
        console.error("Google signup error:", err);
        if (!tryShowServerError(err)) {
          toast.error(
            err.response?.data?.message ||
              "Google login failed. Please try again."
          );
        }
        return;
      }
    } catch (e) {
      console.error("Google login error:", e);
      if (!tryShowServerError(e)) {
        toast.error("Google login failed. Please try again.");
      }
    }
  };

  const handleGoogleError = (err) => {
    console.error("Google login error:", err);
    toast.error("Google login was cancelled or failed.");
  };

  const generateUserId = () => {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }

    // Fallback for environments without randomUUID support
    const randomPart = Math.random().toString(36).substring(2, 10);
    const timestampPart = Date.now().toString(36);
    return `${timestampPart}-${randomPart}`;
  };

  // Shared post-auth success handler for storing and redirecting
  const handlePostAuthSuccess = async ({
    userId,
    email,
    fullName,
    token,
    onboardingChecklist = null,
  }) => {
    try {
      localStorage.setItem("userId", userId);
      localStorage.setItem("token", token);
      localStorage.setItem(
        "userToken",
        JSON.stringify({ userId, email, fullName })
      );
      persistOnboardingChecklist(onboardingChecklist);

      Sentry.setUser({
        email,
        id: userId,
        username: fullName,
      });

      // Dispatch custom login event to open sidebar
      window.dispatchEvent(new CustomEvent("userLoggedIn"));

      const postLoginPath = getPostLoginPath();
      const profileUpdatePath = getProfileUpdatePath();
      const onboardingPath = getOnboardingPath();

      const profileUpdated = localStorage.getItem("profileUpdated");
      if (profileUpdated === null) {
        try {
          const profileResponse = await axios.get(
            `${baseUrl}/v2/profile/user`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          );

          const requiredFields = ["board", "grade", "email"];
          const isProfileComplete = requiredFields.every((field) =>
            profileResponse.data[field]?.trim()
          );
          localStorage.setItem(
            "profileUpdated",
            isProfileComplete ? "true" : "false"
          );
          localStorage.setItem(
            "onboardingComplete",
            isProfileComplete
              ? "true"
              : localStorage.getItem("onboardingComplete") || "false"
          );

          toast.success(
            `Welcome${isProfileComplete ? " back" : ""} ${fullName}!`,
            {
              position: "top-center",
              autoClose: 3000,
              theme: "colored",
            }
          );
          if (typeof setIsLogged === "function") setIsLogged(true);
          if (isProfileComplete) {
            hydrateUserContext(profileResponse.data);
            navigate(postLoginPath);
          } else {
            navigate(onboardingPath);
          }
        } catch (profileError) {
          console.error("Error checking profile (Google):", profileError);
          localStorage.setItem("profileUpdated", "false");
          localStorage.setItem("onboardingComplete", "false");
          if (typeof setIsLogged === "function") setIsLogged(true);
          navigate(onboardingPath);
        }
      } else {
        toast.success(`Welcome back ${fullName}!`, {
          position: "top-center",
          autoClose: 3000,
          theme: "colored",
        });
        if (typeof setIsLogged === "function") setIsLogged(true);
        const onboardingComplete =
          localStorage.getItem("onboardingComplete") === "true";
        const nextPath =
          profileUpdated === "true"
            ? postLoginPath
            : onboardingComplete
            ? profileUpdatePath
            : onboardingPath;
        if (nextPath !== onboardingPath) {
          await syncUserContextFromServer();
        }
        navigate(nextPath);
      }
    } catch (e) {
      console.error("Post-auth handling error:", e);
      toast.error("Something went wrong after login. Please try again.");
    }
  };

  // Capitalize first letter of fullName and set handle to match fullName
  const handleFullNameChange = (e) => {
    const value = e.target.value;
    const capitalizedValue = value.charAt(0).toUpperCase() + value.slice(1);
    setFullName(capitalizedValue);
    setHandle(capitalizedValue);
  };

  const handleSignUp = async () => {
    if (!fullName || !email.trim() || !password.trim() || !handle) {
      setError("Please fill in all fields.");
      return;
    }
    if (password.trim().length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    const userId = generateUserId();
    localStorage.setItem("userId", userId);
    const role = "student";
    const userData = {
      userId,
      fullName,
      email: email.toLowerCase().trim(),
      password: password.trim(),
      role,
      handle,
    };

    setLoading(true);
    setError("");
    setServerErrorDetail(null);

    try {
      const response = await axios.post(`${baseUrl}/v2/auth/signup`, userData);
      if (response.status === 200) {
        toast.success(`Congrats! Account created successfully!`, {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
        });

        logEvent(analytics, "signup_button_click", {
          label: "SignUpButton",
          user_id: email.toLowerCase().trim(),
        });

        setSignupEmail(email.toLowerCase().trim());
        setSignupPassword(password.trim());
        setFullName("");
        setEmail("");
        setPassword("");
        setHandle("");
        setShowOtpModal(true);
      } else {
        setError("Signup failed. Please try again.");
      }
    } catch (err) {
      if (!tryShowServerError(err)) {
        setError(err.response?.data?.detail || "Signup failed.");
      } else {
        setError("");
      }
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (!otp) {
      setError("Please enter the OTP sent to your email.");
      return;
    }

    setServerErrorDetail(null);

    try {
      const response = await axios.post(`${baseUrl}/v2/auth/verify-otp`, {
        email: signupEmail.toLowerCase().trim(),
        otp,
      });
      if (response.status === 200) {
        setShowOtpModal(false);
        let attempts = 0;
        const maxAttempts = 3;
        while (attempts < maxAttempts) {
          try {
            console.log("Auto-login attempt:", {
              email: signupEmail,
              password: signupPassword,
            });
            const loginResponse = await axios.post(`${baseUrl}/v2/auth/login`, {
              email: signupEmail.toLowerCase().trim(),
              password: signupPassword.trim(),
            });
            if (loginResponse.status === 200) {
              const {
                userId,
                email,
                fullName,
                token,
                onboarding_checklist: onboardingChecklist,
                learning,
                learning_current_level: learningCurrentLevel,
              } = loginResponse.data;

              persistLearningState(learning, learningCurrentLevel);
              await handlePostAuthSuccess({
                userId,
                email,
                fullName,
                token,
                onboardingChecklist: onboardingChecklist || null,
              });
              setShowOtpModal(false);
              return;
            }
          } catch (loginErr) {
            console.error(
              "Auto-login error:",
              loginErr.response?.data,
              loginErr.message
            );
            console.log("loginErr - test:", loginErr);
            const detailMessage = loginErr?.response?.data?.detail;
            if (
              typeof detailMessage === "string" &&
              detailMessage.trim() ===
                "Email not verified. Please verify your email."
            ) {
              toast.error("Email not verified. Please verify your email.");
              setIsLogin(false);
              // setShowEmailForm(true);
              // setError(detailMessage);
              return;
            }

            if (
              loginErr.response?.status === 401 &&
              loginErr.response?.data?.message === "Email not verified"
            ) {
              attempts++;
              if (attempts < maxAttempts) {
                await new Promise((resolve) => setTimeout(resolve, 1000));
                continue;
              }
            }
            if (tryShowServerError(loginErr)) {
              setError("");
            } else {
              setError(
                loginErr.response?.data?.message ||
                  "Auto-login failed. Please log in manually."
              );
            }
            setIsLogin(true);
            return;
          }
        }
        setError("Auto-login failed after retries. Please log in manually.");
        setIsLogin(true);
      }
    } catch (err) {
      if (!tryShowServerError(err)) {
        setError(err.response?.data?.detail || "OTP verification failed.");
      } else {
        setError("");
      }
    }
  };

  const handleLogin = async () => {
    //setRemember(true);
    console.log("remember me: true");
    StorageService.setRememberMe(true);

    if (!email.trim() || !password.trim()) {
      setError("Please enter your email and password.");
      return;
    }

    setLoading(true);
    setError("");
    setServerErrorDetail(null);

    try {
      console.log("Login attempt:", {
        email: email.toLowerCase().trim(),
        password: password.trim(),
      });
      const response = await axios.post(`${baseUrl}/v2/auth/login`, {
        email: email.toLowerCase().trim(),
        password: password.trim(),
      });
      if (response.status === 200) {
        const {
          userId,
          email,
          fullName,
          token,
          onboarding_checklist: onboardingChecklist,
          learning: learning,
          learning_current_level: learningCurrentLevel,
        } = response.data;

        // Google Analytics - Set persistent user ID (e.g. from your auth system)
        setUserId(analytics, email);
        logEvent(analytics, "login_button_click", { label: "SignUpButton" });

        localStorage.setItem("socialSignupDetails", "");

        persistLearningState(learning, learningCurrentLevel);
        await handlePostAuthSuccess({
          userId,
          email,
          fullName,
          token,
          onboardingChecklist: onboardingChecklist || null,
        });
      } else {
        setError("Login failed. Invalid credentials.");
      }
    } catch (error) {
      console.error("Login error:", error.response?.data, error.message);
      const detailMessage = error?.response?.data?.detail;
      if (
        typeof detailMessage === "string" &&
        detailMessage.trim() === "User not found. Please sign up first."
      ) {
        toast.error("User not found. Please sign up first.");
        setIsLogin(false);
        setShowEmailForm(true);
        setError(detailMessage);
        return;
      }

      if (
        typeof detailMessage === "string" &&
        detailMessage.trim() === "Email not verified. Please verify your email."
      ) {
        toast.error(
          "Email not verified. Please verify your email. An OTP has been sent to your email. Please verify within 15 minutes."
        );
        //TODO: Show OTP modal
        setSignupEmail(email.toLowerCase().trim());
        setSignupPassword(password.trim());
        setShowOtpModal(true);
        return;
      }

      if (!tryShowServerError(error)) {
        setError(
          error.response?.data?.message ||
            "Login failed. Please check your credentials."
        );
      } else {
        setError("");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen w-full bg-white flex items-center justify-center px-4 py-10 overflow-hidden">
        {showEmailForm ? (
          <div className="w-full max-w-5xl space-y-6 p-2 md:p-5">
            <div className=" rounded-2xl overflow-hidden p-2 md:p-5">
              <div className="w-full justify-center align-center  flex">
                <div className="p-4 md:p-6 flex-col align-center justify-center w-full md:w-8/10 relative">
                  <button
                    type="button"
                    onClick={() => {
                      if (!isLogin) {
                        setIsLogin(true);
                        setError("");
                      } else showLanding();
                    }}
                    className="flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary-light transition absolute left-2 md:left-4 top-2 md:top-2"
                  >
                    <FaChevronLeft className="h-4 w-4" aria-hidden="true" />
                    <span>Back to sign-in options</span>
                  </button>
                  <h2 className="text-2xl font-bold text-primary mb-2 mt-8 md:mt-6">
                    {isLogin ? "Login" : "Sign Up"}
                  </h2>
                  <p className="text-sm text-gray-600 mb-6">
                    {isLogin
                      ? "Welcome back! Please enter your details"
                      : "Welcome! Let's create your account"}
                  </p>
                  {!isLogin && (
                    <div className="mb-4">
                      <input
                        type="text"
                        placeholder="Enter your FullName"
                        value={fullName}
                        onChange={handleFullNameChange}
                        className="w-full p-3 border rounded-md focus:ring-primary-accent focus:border-primary-accent"
                      />
                    </div>
                  )}
                  <div className="mb-4">
                    <input
                      type="text"
                      placeholder="Enter your Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full p-3 border rounded-md focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                  <div className="mb-4 relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full p-3 pr-12 border rounded-md focus:ring-purple-500 focus:border-purple-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                    >
                      {showPassword ? (
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                  {isLogin && (
                    <div className="mb-2 flex items-center justify-between text-sm text-gray-600">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          className="mr-1 rounded border-gray-300 focus:ring-purple-500"
                        />{" "}
                        Remember me
                      </label>
                      <button
                        onClick={() => navigate("/forgot-password")}
                        className="text-primary hover:underline focus:outline-none"
                      >
                        Forgot Password?
                      </button>
                    </div>
                  )}
                  <button
                    className="w-full bg-primary text-white py-3 rounded-md mt-2 hover:bg-primary-light transition focus:outline-none focus:ring-2 focus:ring-primary-accent focus:ring-offset-1 disabled:opacity-70 disabled:cursor-not-allowed"
                    onClick={isLogin ? handleLogin : handleSignUp}
                    disabled={loading}
                  >
                    {isLogin ? "Login" : "Create an account"}
                  </button>
                  {error && (
                    <p className="text-red-500 text-sm mt-2">{error}</p>
                  )}
                  <p className="text-sm text-center text-gray-600 mt-4">
                    {isLogin
                      ? "Don't have an account?"
                      : "Already have an account?"}
                    <button
                      onClick={toggleForm}
                      className="text-primary ml-1 hover:underline focus:outline-none"
                    >
                      {isLogin ? "Sign Up" : "Log In"}
                    </button>
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="relative w-full md:w-7/10 h-full bg-white">
            <div className="absolute -top-28 -left-24 h-60 w-60 rounded-full bg-primary/25 blur-[120px] opacity-80 pointer-events-none"></div>
            <div className="absolute -bottom-32 -right-24 h-72 w-72 rounded-full bg-primary-light/35 opacity-80 pointer-events-none"></div>

            <div className="relative p-[2.5px] rounded-[32px] ">
              <div className="relative overflow-hidden rounded-[30px] p-10 md:p-12 text-center flex flex-col items-center gap-7 h-1/2">
                {/* <div className="pointer-events-none absolute inset-0">
                  <div className="absolute -top-24 -left-16 h-48 w-48 bg-gradient-to-br from-white via-primary/25 to-transparent blur-[120px] opacity-80"></div>
                  <div className="absolute -bottom-28 -right-20 h-64 w-64 bg-gradient-to-tl from-primary-light/70 via-primary/10 to-transparent blur-[150px] opacity-80"></div>
                  <div className="absolute bottom-10 right-6 h-40 w-40 bg-primary/20 rounded-full blur-[110px] opacity-90"></div>
                  <div className="absolute top-6 right-8 h-20 w-36 bg-gradient-to-l from-accent/40 via-transparent to-transparent blur-2xl opacity-80 rotate-6 origin-top"></div>
                </div> */}
                <div className="bg-primary rounded-2xl p-0 shadow-lg ring-1 ring-white/40 relative z-10 overflow-hidden">
                  <div
                    className="absolute inset-[-32%] bg-gradient-to-br from-white/45 via-accent/55 to-primary/60 opacity-80"
                    style={{
                      animation: "ambientPulse 7.5s ease-in-out infinite",
                    }}
                  ></div>
                  <img
                    src="/images/tutor.webp"
                    alt="Aanya"
                    className="w-24 h-28 sm:w-30 sm:h-34 object-cover object-top rounded-2xl"
                  />
                </div>
                <div className="space-y-2 relative z-10">
                  <p className=" uppercase tracking-[0.2em] text-primary font-semibold">
                    Aanya Tutor
                  </p>
                  <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900">
                    Your Personal AI Tutor
                  </h1>
                  <p className="text-base text-slate-600 max-w-xl mx-auto">
                    Sign in to start exploring or continue your learning
                    journey.
                  </p>
                </div>
                <div className="w-full max-w-lg space-y-4 relative z-10">
                  <div className="mt-6 w-full max-w-sm mx-auto flex justify-center items-center">
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={handleGoogleError}
                      ux_mode="popup"
                      shape="rectangular"
                      text="continue_with"
                      theme="filled_blue"
                      size="large"
                      width="100%"
                      data-width="100%"
                      style={{ width: "100%" }}
                    />
                  </div>
                  {/* <button
                    type="button"
                    onClick={() => navigate("/login/mobile")}
                    className="w-full max-w-sm mx-auto bg-primary  text-white text-base py-3 rounded-md font-semibold flex items-center justify-center gap-2 shadow-lg hover:brightness-105 transition"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M7 4h10a2 2 0 012 2v12a2 2 0 01-2 2H7a2 2 0 01-2-2V6a2 2 0 012-2z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 18h.01M9 6h6"
                      />
                    </svg>
                    Continue with Mobile Number
                  </button> */}
                  <button
                    type="button"
                    onClick={showEmailLogin}
                    className="mt-6 w-full max-w-sm mx-auto bg-white text-primary text-sm font-semibold py-3 px-4 rounded-md underline transition focus:outline-none focus:ring-2 focus:ring-primary-light focus:ring-offset-1"
                  >
                    Or continue with Email
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {showOtpModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/90 z-50">
          <div className="bg-white p-6 rounded-xl shadow-md w-96 relative">
            <button
              type="button"
              onClick={() => {
                setShowOtpModal(false);
                setOtp("");
                setError("");
              }}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 focus:outline-none"
              aria-label="Close"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
            <h2 className="text-xl font-semibold text-primary mb-4">
              Verify OTP
            </h2>
            <p className="text-sm text-gray-700 mb-3">
              We've sent a 6-digit OTP to your email. Please enter it below to
              verify your account.
            </p>
            <input
              type="text"
              maxLength="6"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              placeholder="Enter OTP"
              className="w-full p-3 border rounded-md focus:ring-purple-500 focus:border-purple-500 mb-4"
            />
            {error && <p className="text-red-500 text-sm mb-2">{error}</p>}
            <div className="flex justify-end">
              <button
                className="bg-primary text-white py-2 px-4 rounded-md hover:bg-primary-accent transition"
                onClick={verifyOtp}
              >
                Verify
              </button>
            </div>
          </div>
        </div>
      )}
      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-8 flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            <p className="text-gray-700 font-medium">
              {isLogin ? "Logging in..." : "Creating account..."}
            </p>
          </div>
        </div>
      )}
      <AuthServerErrorModal
        isOpen={Boolean(serverErrorDetail)}
        detail={serverErrorDetail}
        onClose={() => setServerErrorDetail(null)}
      />
    </>
  );
}

AuthForm.propTypes = {
  setIsLogged: PropTypes.func.isRequired,
};
